import { Component, Input, forwardRef, ContentChildren, QueryList, AfterContentInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';

export type SelectSize = 'sm' | 'md' | 'lg';

export interface SelectOption {
  value: any;
  label: string;
  disabled?: boolean;
  warning?: boolean;
}

@Component({
  selector: 'app-select',
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
  ],
  styles: [`
    select option {
      padding: 12px 16px;
      background-color: white;
      color: #1f2937;
      font-size: 14px;
    }

    select option:hover {
      background-color: #f3f4f6;
    }

    select option:checked {
      background-color: #4A90E2;
      color: white;
    }

    select option:disabled {
      color: #9ca3af;
      background-color: #f9fafb;
    }
  `],
  template: `
    <div class="w-full">
      <!-- Label (only shown if label is provided) -->
      <label
        *ngIf="label"
        [for]="selectId"
        class="block text-sm font-medium text-gray-900 mb-2"
      >
        {{ label }}
        <span *ngIf="required" class="text-red-500">*</span>
      </label>

      <!-- Select Container -->
      <div class="relative">
        <select
          [id]="selectId"
          [disabled]="disabled"
          [required]="required"
          [(ngModel)]="value"
          (blur)="onTouched()"
          (change)="onSelectChange($event)"
          [ngClass]="getSelectClasses()"
          [attr.aria-label]="ariaLabel || label || placeholder"
          [attr.aria-describedby]="error || hint ? selectId + '-description' : null"
          [attr.aria-invalid]="error ? 'true' : null"
        >
          <!-- Placeholder option (only shown if there's no option with empty value) -->
          <option 
            *ngIf="!hasEmptyValueOption()" 
            value="" 
            [disabled]="!label && required"
          >
            {{ placeholder }}
          </option>

          <!-- Options from array -->
          <option
            *ngFor="let option of processedOptions"
            [value]="getOptionValue(option)"
            [disabled]="isOptionDisabled(option)"
          >
            {{ getWarningPrefix(option) }}{{ getOptionLabel(option) }}
          </option>

          <!-- Projected content (ng-content for custom options) -->
          <ng-content></ng-content>
        </select>

        <!-- Chevron Icon -->
        <div
          class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
        >
          <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      <!-- Helper Text / Error -->
      <div
        *ngIf="error || hint"
        [id]="selectId + '-description'"
        class="mt-1 text-sm"
        [ngClass]="{
          'text-red-500': error,
          'text-gray-500': !error && hint
        }"
      >
        {{ error || hint }}
      </div>
    </div>
  `,
})
export class SelectComponent implements ControlValueAccessor {
  @Input() label?: string;
  @Input() placeholder: string = 'Selecione';
  @Input() hint?: string;
  @Input() error?: string;
  @Input() options: SelectOption[] | any[] = [];
  @Input() optionValue?: string; // Nome da propriedade que contém o valor (ex: 'id')
  @Input() optionLabel?: string; // Nome da propriedade que contém o label (ex: 'nomeCompleto')
  @Input() size: SelectSize = 'md';
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() ariaLabel?: string;
  @Input() selectId: string = `select-${Math.random().toString(36).substr(2, 9)}`;
  @Input() className: string = '';

  value: any = '';
  onChange: any = () => {};
  onTouched: any = () => {};

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onSelectChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    let newValue: any = select.value;
    
    // Se o valor é string vazia, verificar se há uma opção com value: '' nas options
    // Se houver, manter como string vazia, senão converter para null
    if (newValue === '') {
      if (!this.hasEmptyValueOption()) {
        newValue = null;
      }
    }
    
    // Se estamos usando optionValue, converter string de volta para tipo original se necessário
    if (this.optionValue && newValue !== null && this.options.length > 0) {
      const option = this.options.find(opt => {
        const optValue = this.getOptionValue(opt);
        return String(optValue) === String(newValue);
      });
      
      if (option) {
        const originalValue = option[this.optionValue];
        // Se o valor original é numérico, converter de volta
        if (typeof originalValue === 'number') {
          this.value = Number(newValue);
        } else {
          this.value = originalValue;
        }
      } else {
        this.value = newValue;
      }
    } else {
      this.value = newValue;
    }
    
    this.onChange(this.value);
  }

  /**
   * Retorna as opções processadas
   * Se optionValue e optionLabel estão definidos, retorna options como array genérico
   * Caso contrário, retorna options como SelectOption[]
   */
  get processedOptions(): any[] {
    return this.options || [];
  }

  /**
   * Obtém o valor de uma opção
   */
  getOptionValue(option: any): any {
    if (this.optionValue) {
      // Modo genérico: extrair propriedade do objeto
      const value = option[this.optionValue];
      // Converter null/undefined para string vazia para compatibilidade com select HTML
      return value === null || value === undefined ? '' : value;
    } else {
      // Modo SelectOption: usar propriedade value
      const value = (option as SelectOption).value;
      return value === null || value === undefined ? '' : value;
    }
  }

  /**
   * Obtém o label de uma opção
   */
  getOptionLabel(option: any): string {
    if (this.optionLabel) {
      // Modo genérico: extrair propriedade do objeto
      // Suporta múltiplas propriedades separadas por '|' (ex: "nomeCompleto|email")
      const labels = this.optionLabel.split('|').map(prop => {
        const parts = prop.trim().split('.');
        let value = option;
        for (const part of parts) {
          value = value?.[part];
          if (value === undefined || value === null) break;
        }
        return value ?? '';
      }).filter(v => v);
      
      // Se há mais de um label, formatar como "primeiro (segundo)"
      if (labels.length > 1) {
        return `${labels[0]} (${labels.slice(1).join(', ')})`;
      }
      return labels.join(' ') || '';
    } else {
      // Modo SelectOption: usar propriedade label
      return (option as SelectOption).label ?? '';
    }
  }

  /**
   * Verifica se uma opção está desabilitada
   */
  isOptionDisabled(option: any): boolean {
    if (this.optionValue) {
      // Modo genérico: verificar se tem propriedade disabled
      return option.disabled === true;
    } else {
      // Modo SelectOption: usar propriedade disabled
      return (option as SelectOption).disabled === true;
    }
  }

  /**
   * Obtém o prefixo de aviso (⚠️) se existir
   */
  getWarningPrefix(option: any): string {
    if (this.optionValue) {
      // Modo genérico: não suporta warning
      return '';
    } else {
      // Modo SelectOption: verificar propriedade warning
      return (option as SelectOption).warning ? '⚠️ ' : '';
    }
  }

  /**
   * Verifica se já existe uma opção com valor vazio nas options
   */
  hasEmptyValueOption(): boolean {
    if (!this.options || this.options.length === 0) {
      return false;
    }
    
    return this.processedOptions.some(option => {
      const value = this.getOptionValue(option);
      return value === '' || value === null || value === undefined;
    });
  }

  getSelectClasses(): string {
    const baseClasses =
      'w-full border rounded-lg transition-colors focus:outline-none focus:ring-2 focus:border-transparent appearance-none pr-10';

    const sizeClasses = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-3 py-2 text-base',
      lg: 'px-4 py-3 text-lg',
    };

    const stateClasses = this.error
      ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
      : 'border-gray-300 focus:ring-[#4A90E2] focus:ring-[#4A90E2]';

    const disabledClasses = this.disabled
      ? 'bg-gray-100 cursor-not-allowed opacity-60'
      : 'bg-gray-50 hover:bg-white';

    return `${baseClasses} ${sizeClasses[this.size]} ${stateClasses} ${disabledClasses} ${this.className}`.trim();
  }
}
